import { convexQuery } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { FunctionReturnType } from 'convex/server'
import { IconInboxOutline18 } from 'nucleo-ui-outline-18'
import { useState } from 'react'
import { CatalogStatusPill } from '~/components/admin/catalog-bits'
import { DetailBadge, StatBox } from '~/components/admin/detail-bits'
import { Button } from '~/components/ui/button'
import { CollapseChevron } from '~/components/ui/collapse-chevron'
import { EmptyState } from '~/components/ui/empty-state'
import { BackLink, PageHeader } from '~/components/ui/page-header'
import { Spinner } from '~/components/ui/spinner'
import { useGrowingPage } from '~/hooks/use-growing-page'
import { formatAbsoluteTime, formatDuration, formatRelativeTime } from '~/lib/datetime'
import { titleFor } from '~/lib/route-title'
import { cn } from '~/lib/utils'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'

export const Route = createFileRoute('/admin/catalog/imports')({
  component: AdminCatalogImports,
  head: () => ({ meta: [{ title: titleFor(msg`Import log`) }] }),
})

const PAGE_SIZE = 50

type BatchRow = FunctionReturnType<typeof api.catalogAdminQueries.listBatches>['page'][number]

/** The products an accepted batch touched, loaded when its row is opened. */
const TouchedProducts = ({ batchId }: { batchId: Id<'catalogImportBatches'> }) => {
  const { data } = useQuery(convexQuery(api.catalogAdminQueries.batchTouchedProducts, { batchId }))
  if (data === undefined) {
    return (
      <div className="flex justify-center py-3">
        <Spinner />
      </div>
    )
  }
  if (data.length === 0) {
    return (
      <p className="py-2 text-muted-foreground text-sm">
        <Trans>This batch touched no product.</Trans>
      </p>
    )
  }
  return (
    <ul className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
      {data.map((product) => (
        <li className="flex items-center gap-2 py-1 text-sm" key={product._id}>
          <Link
            className="min-w-0 truncate text-primary hover:underline"
            params={{ productId: product._id }}
            to="/admin/catalog/$productId"
          >
            {product.name}
          </Link>
          <span className="truncate text-muted-foreground text-xs">{product.brandName}</span>
          <CatalogStatusPill status={product.status} />
        </li>
      ))}
    </ul>
  )
}

const BatchRowItem = ({ batch }: { batch: BatchRow }) => {
  const { t, i18n } = useLingui()
  const [open, setOpen] = useState(false)
  const rejected = batch.status === 'rejected'
  return (
    <li>
      <button
        aria-expanded={open}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-left text-sm hover:bg-accent/50"
        onClick={() => setOpen((value) => !value)}
        type="button"
      >
        <CollapseChevron expanded={open} />
        <span
          className="w-40 shrink-0 tabular-nums"
          title={formatAbsoluteTime(batch.receivedAt, i18n)}
        >
          {formatAbsoluteTime(batch.receivedAt, i18n)}
        </span>
        <DetailBadge tone={rejected ? 'destructive' : 'primary'}>
          {rejected ? t`rejected` : t`accepted`}
        </DetailBadge>
        <span className="text-muted-foreground text-xs tabular-nums">
          v{batch.schemaVersion} · {i18n.number(batch.observationCount)} {t`obs`}
        </span>
        {rejected ? (
          <span className="min-w-0 flex-1 truncate text-muted-foreground text-xs">
            {batch.rejections[0]?.reason ?? ''}
          </span>
        ) : (
          <span className="min-w-0 flex-1 text-muted-foreground text-xs tabular-nums">
            <Trans>
              {i18n.number(batch.created)} created · {i18n.number(batch.updated)} updated ·{' '}
              {i18n.number(batch.unchanged)} unchanged · {i18n.number(batch.statusRows)} status
            </Trans>
          </span>
        )}
        <span className="text-muted-foreground text-xs tabular-nums">
          {formatDuration(batch.durationMs)}
        </span>
      </button>
      {open ? (
        <div className="border-border border-t bg-muted/40 px-4 py-3">
          {rejected ? (
            batch.rejections.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                <Trans>No reason was recorded.</Trans>
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground text-xs">
                  <tr>
                    <th className="py-1 pr-3 font-medium">#</th>
                    <th className="py-1 pr-3 font-medium">{t`Path`}</th>
                    <th className="py-1 font-medium">{t`Reason`}</th>
                  </tr>
                </thead>
                <tbody>
                  {batch.rejections.map((rejection, index) => (
                    <tr key={`${rejection.path}-${index}`}>
                      <td className="py-1 pr-3 text-muted-foreground tabular-nums">
                        {rejection.index ?? '—'}
                      </td>
                      <td className="py-1 pr-3 font-mono text-xs">{rejection.path || '—'}</td>
                      <td className="py-1">{rejection.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            <TouchedProducts batchId={batch._id} />
          )}
        </div>
      ) : null}
    </li>
  )
}

/** `/admin/catalog/imports` — one row per POST, what landed and what bounced. */
function AdminCatalogImports() {
  const { t, i18n } = useLingui()
  const { paginationOpts, loadMore } = useGrowingPage(PAGE_SIZE)
  const { data } = useQuery(convexQuery(api.catalogAdminQueries.listBatches, { paginationOpts }))
  const { data: stats } = useQuery(convexQuery(api.catalogAdminQueries.batchStats, {}))

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        above={<BackLink to="/admin/catalog">{t`Product catalog`}</BackLink>}
        title={t`Import log`}
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4">
        {stats === undefined ? null : (
          <div className="grid grid-cols-2 gap-3 py-4 sm:grid-cols-4">
            <StatBox label={t`Batches, last 24 h`} value={i18n.number(stats.last24h)} />
            <StatBox label={t`Batches, last 7 d`} value={i18n.number(stats.last7d)} />
            <StatBox label={t`Rejected, last 7 d`} value={i18n.number(stats.rejected7d)} />
            <StatBox
              label={t`Last rejection`}
              value={
                stats.lastRejection === null
                  ? '—'
                  : formatRelativeTime(stats.lastRejection.receivedAt, i18n)
              }
            />
          </div>
        )}
        {stats?.lastRejection?.reason ? (
          <p className={cn('mb-3 text-muted-foreground text-xs')}>
            <Trans>Last rejection reason:</Trans> {stats.lastRejection.reason}
          </p>
        ) : null}
        {data === undefined ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : data.page.length === 0 ? (
          <EmptyState
            description={t`Every POST to the import endpoint lands here, accepted or rejected.`}
            icon={IconInboxOutline18}
            title={t`No batches yet`}
          />
        ) : (
          <>
            <ol className="divide-y divide-border rounded-xl border border-border bg-card">
              {data.page.map((batch) => (
                <BatchRowItem batch={batch} key={batch._id} />
              ))}
            </ol>
            {data.isDone ? null : (
              <div className="flex justify-center py-3">
                <Button onClick={loadMore} size="sm" type="button" variant="outline">
                  <Trans>Load more</Trans>
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
